import React from 'react';
import Table, { TableProps } from 'components/Table';
import utils from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import selectors from 'state/selectors';
import actions from 'state/actions';
import { Wrapper, Title } from '../../styles';
import styled from 'styled-components/macro';
import { GetMailMonitor } from 'models/response';

const SubTitle = styled.div`
  font-size: 24px;
  color: ${({ theme }) => theme.colorPalette.grey.grey7};
  margin: 25px 0px;
`;

interface Props {
  hasHistory: boolean;
}

const Mail = (props: Props) => {
  const { hasHistory } = props;
  const t = utils.intl.useTranslator();
  const mainRows = useSelector(selectors.normalizer.getMonitorMailRows);
  const historyRows = useSelector(selectors.normalizer.getMonitorMailHistory);
  const { dateFormat } = useSelector(selectors.user.getUserPreference);
  const dispatch = useDispatch();
  const wfHistoryRows = useSelector(selectors.normalizer.getMonitorMailWfHistory);
  const userPreference = useSelector(selectors.user.getUserPreference);
  const selectedRows = useSelector(selectors.normalizer.getSelectedMonitorRow);
  const pdf = utils.hooks.usePDFImage(selectedRows.length > 0 ? selectedRows[0].protocol : null);
  const mainColumns: TableProps['columns'] = [
    { accessor: 'company', Header: t('company') },
    { accessor: 'documentName', Header: t('documentName') },
    { accessor: 'documentType', Header: t('documentType') },
    {
      accessor: 'acquisitionDate',
      Header: t('acquisitionDate'),
      filterType: 'date',
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
      dateFormat: dateFormat,
    },
    { accessor: 'protocolIn', Header: t('protocolIn') },
    { accessor: 'protocol', Header: t('protocol') },
    { accessor: 'bucketPath', Header: t('bucketPath') },
    { accessor: 'fromAdr', Header: t('fromAdr') },
    { accessor: 'toAdr', Header: t('toAdr') },
    { accessor: 'subject', Header: t('subject') },
    { accessor: 'position', Header: t('position') },
  ];

  const historyColumns: TableProps['columns'] = [
    { accessor: 'company', Header: t('company') },
    { accessor: 'documentName', Header: t('documentName') },
    { accessor: 'documentType', Header: t('documentType') },
    { accessor: 'currentUser', Header: t('currentUser') },
    { accessor: 'currentUsername', Header: t('currentUsername') },
    { accessor: 'senderUser', Header: t('senderUser') },
    { accessor: 'senderUsername', Header: t('senderUsername') },
    {
      accessor: 'insDate',
      Header: t('insDate'),
      filterType: 'date',
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
      dateFormat: dateFormat,
    },
    { accessor: 'protocolIn', Header: t('protocolIn') },
    { accessor: 'bucketPath', Header: t('bucketPath') },
    { accessor: 'fromAdr', Header: t('fromAdr') },
    { accessor: 'toAdr', Header: t('toAdr') },
    { accessor: 'subject', Header: t('subject') },
    { accessor: 'position', Header: t('position') },
    { accessor: 'priority', Header: t('priority') },
    { accessor: 'status', Header: t('status') },
    { accessor: 'incChannel', Header: t('incChannel') },
  ];

  const wfHistoryColumns: TableProps['columns'] = [
    {
      accessor: 'sendDate',
      Header: t('sendDate'),
      filterType: 'date',
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
      dateFormat: dateFormat,
    },
    { accessor: 'sender.username', Header: t('username') },
    { accessor: 'type', Header: t('type') },
    { accessor: 'typeDescription', Header: t('typeDescription') },
    { accessor: 'rejectionReason', Header: t('rejectionReason') },
    { accessor: 'note', Header: t('note') },
    { accessor: 'causalCode', Header: t('causalCode') },
  ];

  const onSelection = (selection: GetMailMonitor[]) => {
    if (!selection.length) {
      dispatch(actions.normalizer.setMonitorMailHistory([]));
      dispatch(actions.normalizer.setMonitorMailWfHistory([]));
    }
    dispatch(actions.normalizer.setSelectedMonitorRow(selection));
  };

  return (
    <Wrapper>
      <Title>
        <h2>{t('Mail_Address_Monitor')}</h2>
      </Title>
      {mainRows.length > 0 ? (
        <>
          <Table
            id="normalizer-monitor-mail-table"
            onSelection={onSelection}
            columns={mainColumns}
            rows={mainRows}
            hasSelection
            hasFilter
            hasSort
            hasPagination
            hasToolbar
          />
          {pdf}
        </>
      ) : (
        <div>{t('no_data')}</div>
      )}
      {historyRows.length > 0 && hasHistory ? (
        <>
          <SubTitle>{t('document_history')}</SubTitle>
          <Table
            id="normalizer-monitor-mail-history-table"
            columns={historyColumns}
            rows={historyRows}
            hasSort
            hasFilter
            hasToolbar
          />
        </>
      ) : null}
      {wfHistoryRows.length > 0 && hasHistory ? (
        <>
          <SubTitle>{t('workflow_history')}</SubTitle>
          <Table
            id="normalizer-monitor-mail-wf-history-table"
            columns={wfHistoryColumns}
            rows={wfHistoryRows}
            hasSort
            hasFilter
            hasToolbar
          />
        </>
      ) : null}
    </Wrapper>
  );
};

export default Mail;
